import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowUpRight } from 'lucide-react'
import EditorialHero from '@/components/editorial/EditorialHero'
import SectionHeader from '@/components/editorial/SectionHeader'
import SystemShowcase from '@/components/editorial/SystemShowcase'
import ApplicationEnquiry from '@/components/editorial/ApplicationEnquiry'
import Marquee from '@/components/preview/Marquee'
import Reveal from '@/components/preview/Reveal'

export const Route = createFileRoute('/playground/')({
  component: PlaygroundPage,
  head: () => {
    const url = 'https://www.suriyaprakash.in/playground'
    const title = 'React Component Systems & Demos | Suriyaprakash Somu'
    const description =
      'Interactive React systems from production-style apps: a DataTable, schema-driven form builder and visual QueryBuilder, with live demos and typed contracts.'

    return {
      meta: [
        { title },
        { name: 'title', content: title },
        { name: 'description', content: description },
        { name: 'robots', content: 'index, follow' },
        { name: 'author', content: 'Suriyaprakash Somu' },
        { name: 'geo.region', content: 'IN-TN' },
        { name: 'geo.placename', content: 'Erode, Tamil Nadu, India' },
        {
          name: 'keywords',
          content:
            'React playground, UI component demo, DataTable component, SchemaForm component, TanStack Table, schema based forms, TypeScript portfolio, Suriyaprakash Somu, full stack developer Erode',
        },
        {
          name: 'ai-summary',
          content:
            "This playground showcases Suriyaprakash Somu's production-oriented React components: a sortable paginated DataTable, a visual SchemaForm builder with live preview, and a schema-driven QueryBuilder with chip and tree filters.",
        },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:locale', content: 'en_IN' },
        { property: 'og:type', content: 'website' },
        {
          property: 'og:image',
          content: 'https://www.suriyaprakash.in/og/default.png',
        },
        { property: 'og:url', content: url },
        { property: 'og:site_name', content: 'Suriyaprakash Somu' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        {
          name: 'twitter:image',
          content: 'https://www.suriyaprakash.in/og/default.png',
        },
      ],
      links: [
        { rel: 'canonical', href: url },
        {
          rel: 'alternate',
          type: 'application/rss+xml',
          title: 'RSS',
          href: '/rss.xml',
        },
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: title,
            description,
            url,
            creator: {
              '@type': 'Person',
              name: 'Suriyaprakash Somu',
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'Erode',
                addressRegion: 'Tamil Nadu',
                addressCountry: 'IN',
              },
            },
            hasPart: [
              {
                '@type': 'SoftwareApplication',
                name: 'DataTable Playground',
                applicationCategory: 'DeveloperApplication',
                url: `${url}/datatable`,
              },
              {
                '@type': 'SoftwareApplication',
                name: 'SchemaForm Builder',
                applicationCategory: 'DeveloperApplication',
                url: `${url}/schemaform`,
              },
              {
                '@type': 'SoftwareApplication',
                name: 'QueryBuilder Playground',
                applicationCategory: 'DeveloperApplication',
                url: `${url}/querybuilder`,
              },
            ],
          }),
        },
      ],
    }
  },
})

function PlaygroundPage() {
  const capabilities = [
    'Sort, paginate, select, and reorder DataTable columns',
    'Build multi-field forms from a visual palette',
    'Arrange fields into sections and responsive grid spans',
    'Export generated Zod schema code',
    'Compose quick filters and nested AND/OR trees',
    'Copy live filter JSON into the DataTable contract',
  ]

  return (
    <div className="min-h-screen bg-cream-50">
      <EditorialHero
        eyebrow="Interactive systems"
        title="The foundations behind"
        accent="business applications."
        description="Three reusable React systems I use across IndianContext and VetNest for working with records, collecting information and finding data. Try the demos, then explore the implementation behind each one."
        meta={
          <p className="preview-mono text-[10px] uppercase leading-6 tracking-[0.18em] text-cream-50/45">
            TanStack Table
            <br />
            Zod metadata
            <br />
            TypeScript contracts
          </p>
        }
      />

      <Marquee
        duration={28}
        className="border-y border-forest-strong/15 bg-sage-200 py-4 text-forest-strong"
        itemClassName="text-base font-extrabold uppercase tracking-[0.14em] sm:text-lg"
        items={[
          'Data systems',
          'Schema forms',
          'Query composition',
          'Permission-aware UI',
          'Production patterns',
        ]}
      />

      <section className="bg-sage-100 py-20 text-ink sm:py-28">
        <div className="container-custom">
          <SectionHeader
            eyebrow="Component systems"
            title="Proof, not"
            accent="promises."
            description="Each preview links to an interactive demo with sample data, practical use cases and technical details. They are the building blocks behind the admin workspaces in my case studies, not standalone products."
          />
          <div className="mt-12 sm:mt-16">
            <SystemShowcase />
          </div>
        </div>
      </section>

      <section className="bg-ink py-20 text-cream-50 sm:py-28">
        <div className="container-custom">
          <SectionHeader
            tone="dark"
            eyebrow="Working principles"
            title="Explore the behaviour."
            accent="Inspect the implementation."
            description="See how each system handles its data and interactions, then review the typed contracts and usage examples. The demos run in your browser rather than against a production backend."
          />
          <div className="mt-12 border-b border-white/15 sm:mt-16">
            {capabilities.map((capability, index) => (
              <Reveal key={capability} delay={index * 60}>
                <div className="grid grid-cols-[3rem_1fr_auto] items-center gap-4 border-t border-white/15 py-6 sm:grid-cols-[5rem_1fr_auto] sm:py-8">
                  <span className="preview-mono text-xs font-bold text-sage-200/55">
                    0{index + 1}
                  </span>
                  <p className="font-heading text-2xl leading-tight tracking-[-0.02em] sm:text-4xl">
                    {capability}
                  </p>
                  <ArrowUpRight className="h-4 w-4 text-sage-200/60" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream-50 py-20 text-ink sm:py-28">
        <div className="container-custom">
          <SectionHeader
            eyebrow="One continuous system"
            title="Entry, discovery,"
            accent="decision."
            description="Forms collect information, filters narrow the records and tables make them usable. These demos show complementary patterns I reuse across applications; each one is a separate browser-based demonstration."
            action={
              <Link
                to="/playground/datatable"
                className="preview-link-sweep preview-mono focus-ring inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-forest"
              >
                Try the DataTable <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            }
          />
        </div>
      </section>
      <ApplicationEnquiry />
    </div>
  )
}
